/**
 * Sentry Configuration for Life Alert AI Frontend
 *
 * This module initializes and configures Sentry for error tracking,
 * performance monitoring, and session replay in the React application.
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.PROD;

/**
 * Initialize Sentry for the frontend application
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    console.warn('[Sentry] No DSN configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment configuration
    environment: isProd ? 'production' : 'development',

    // Performance Monitoring
    tracesSampleRate: isProd ? 0.2 : 1.0, // 20% in prod, 100% in dev

    // Session Replay for debugging user issues
    replaysSessionSampleRate: isProd ? 0.1 : 1.0, // 10% in prod
    replaysOnErrorSampleRate: 1.0, // 100% when errors occur

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out common non-critical errors
    ignoreErrors: [
      // Browser extension errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that don't indicate app issues
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // React-specific noise
      'Loading chunk',
      // User cancellations
      'AbortError',
    ],

    // Don't send PII by default
    sendDefaultPii: false,

    // Release tracking (set during build)
    release: import.meta.env.VITE_SENTRY_RELEASE || 'life-alert-ai@1.0.0',

    // Before send hook for additional filtering
    beforeSend(event) {
      // Don't send errors in development unless explicitly enabled
      if (!isProd && !import.meta.env.VITE_SENTRY_DEV_ENABLED) {
        return null;
      }
      return event;
    },
  });

  console.info('[Sentry] Initialized successfully');
}

/**
 * Capture a custom error with additional context
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
): string | undefined {
  if (!SENTRY_DSN) return undefined;

  return Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a custom message for tracking
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): string | undefined {
  if (!SENTRY_DSN) return undefined;

  return Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Set user context for error tracking
 */
export function setUser(user: {
  id?: string;
  email?: string;
  username?: string;
} | null): void {
  if (!SENTRY_DSN) return;

  if (user) {
    Sentry.setUser(user);
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(breadcrumb: {
  category: string;
  message: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set custom tag for filtering in Sentry dashboard
 */
export function setTag(key: string, value: string): void {
  if (!SENTRY_DSN) return;

  Sentry.setTag(key, value);
}

/**
 * Start a performance transaction
 */
export function startTransaction(name: string, op: string) {
  if (!SENTRY_DSN) return null;

  return Sentry.startInactiveSpan({
    name,
    op,
  });
}

// Re-export Sentry's ErrorBoundary for use in React
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Re-export withProfiler for component performance tracking
export const withSentryProfiler = Sentry.withProfiler;

export default Sentry;
